import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Client for the public (magic-link) opportunity API. The invitation token
 * arrives in the URL FRAGMENT (decision 5) and is held in memory only — it
 * is passed via the x-invitation-token header and must NEVER be written to
 * storage, query strings, or navigation targets.
 */

export type OpportunityState = "ok" | "not_found" | "closed" | "expired" | "error";

export interface AnsweredQuestion {
  questionId: string;
  question: string;
  answer: string;
  answeredAt: string;
}

export interface Money {
  currency: "USD";
  amountCents: number;
}

export interface QuoteView {
  type: "fixed" | "range" | "needs_visit";
  amount?: Money;
  minAmount?: Money;
  maxAmount?: Money;
  notes?: string;
  availabilityWindows?: { date: string; note?: string }[];
  submittedAt: string;
}

export type JobStatus =
  | "SELECTED"
  | "ACCEPTED"
  | "SCHEDULED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED";

/** The winner's job (unit 18) — only present on the selected invitation. */
export interface JobView {
  jobId: string;
  status: JobStatus;
  scheduledFor?: string;
  history: { status: JobStatus; at: string; note?: string }[];
  updatedAt: string;
}

export interface JobStatusUpdateBody {
  status: Exclude<JobStatus, "SELECTED">;
  scheduledFor?: string;
  note?: string;
}

export interface BinderFactView {
  label: string;
  value: string | number | boolean;
  valueType: string;
  unit?: string;
}

/** One Property File entry the customer shared with this request. */
export interface AttachedBinderEntryView {
  entryId: string;
  title: string;
  entryType: string;
  locationLabel?: string;
  notes?: string;
  facts?: Record<string, BinderFactView>;
  dates?: Record<string, { value: string; precision: string; estimated?: boolean }>;
  /** mediaId -> short-lived presigned URL. */
  photos: Record<string, string>;
}

/**
 * Customer-shared Property Insights (rounded upstream — every figure is an
 * estimate). Only present when the customer opted in on a scope-relevant
 * category. All fields optional; an empty object renders nothing.
 */
export interface PropertyInsightsView {
  lotSizeSqft?: number;
  homeSqft?: number;
  mowableAreaSqft?: number;
  propertyType?: string;
  yearBuilt?: number;
}

export interface OpportunityView {
  state: "ok";
  invitation: { invitationId: string; status: string; tokenExpiresAt: string };
  opportunity: {
    customerFirstName: string;
    serviceCategory: string;
    urgency: string;
    description: string;
    location: { city: string; state: string; postalCode: string };
    answeredQuestions: AnsweredQuestion[];
    photos: Record<string, string>;
    photosExpireInSeconds: number;
    attachedBinder?: AttachedBinderEntryView[];
    propertyInsights?: PropertyInsightsView | null;
  };
  quote: QuoteView | null;
  myQuestion: { question: string; askedAt: string; answered: boolean } | null;
  job: JobView | null;
}

export type OpportunityResult = OpportunityView | { state: Exclude<OpportunityState, "ok"> };

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL;
}

async function publicFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
  return fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      "x-invitation-token": token,
      ...(init?.body ? { "content-type": "application/json" } : {}),
    },
  });
}

function stateFor(status: number): Exclude<OpportunityState, "ok"> {
  if (status === 404) return "not_found";
  if (status === 410) return "closed";
  if (status === 403) return "expired";
  return "error";
}

export function useOpportunity(invitationId: string | undefined, token: string | null) {
  return useQuery<OpportunityResult>({
    queryKey: ["opportunity", invitationId],
    enabled: Boolean(invitationId && token),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}`, token!);
      if (!res.ok) return { state: stateFor(res.status) };
      return (await res.json()) as OpportunityView;
    },
  });
}

export type SubmitQuoteBody =
  | {
      type: "fixed";
      amount: Money;
      notes?: string;
      availabilityWindows?: { date: string; note?: string }[];
    }
  | {
      type: "range";
      minAmount: Money;
      maxAmount: Money;
      notes?: string;
      availabilityWindows?: { date: string; note?: string }[];
    }
  | {
      type: "needs_visit";
      notes?: string;
      availabilityWindows?: { date: string; note?: string }[];
    };

export function useSubmitQuote(invitationId: string | undefined, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitQuoteBody) => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/quotes`, token!, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? "Could not submit your quote");
      }
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["opportunity", invitationId] }),
  });
}

/** Job lifecycle from the magic link (unit 18) — the winner's affordance. */
export function useUpdateJobStatus(invitationId: string | undefined, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: JobStatusUpdateBody) => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/job-status`, token!, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? "Could not update the job status");
      }
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["opportunity", invitationId] }),
  });
}

export function useAskQuestion(invitationId: string | undefined, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (question: string) => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/question`, token!, {
        method: "POST",
        body: JSON.stringify({ question }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? "Could not send your question");
      }
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["opportunity", invitationId] }),
  });
}

/** "425" / "425.50" -> cents. Returns null for anything unparseable. */
export function dollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, "").replaceAll(",", "");
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [dollars, cents = "0"] = trimmed.split(".");
  const value = Number(dollars) * 100 + Number(cents.padEnd(2, "0"));
  return value > 0 ? value : null;
}

export function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export const urgencyLabels: Record<string, string> = {
  emergency: "Emergency — within 24 hours",
  urgent: "Urgent — within a few days",
  normal: "Within a couple of weeks",
  flexible: "Flexible timing",
};

/** Humanized property types for the customer-shared insights block. */
export const propertyTypeLabels: Record<string, string> = {
  single_family: "Single family",
  multi_family: "Multi-family",
  condo: "Condo",
  townhouse: "Townhouse",
  commercial: "Commercial",
  other: "Other",
};

export const categoryLabels: Record<string, string> = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  hvac: "HVAC",
  roofing: "Roofing",
  landscaping: "Landscaping",
  painting: "Painting",
  cleaning: "Cleaning",
  appliance_repair: "Appliance repair",
  general_handyman: "General handyman",
  pest_control: "Pest control",
  other: "General services",
};

export interface ThreadMessage {
  messageId: string;
  sender: "customer" | "contractor";
  body: string;
  sentAt: string;
}

export function useMessages(invitationId: string | undefined, token: string | null) {
  return useQuery({
    queryKey: ["opportunity", invitationId, "messages"],
    enabled: Boolean(invitationId && token),
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/messages`, token!);
      if (!res.ok) return { threadOpen: false, messages: [] as ThreadMessage[] };
      return (await res.json()) as { threadOpen: boolean; messages: ThreadMessage[] };
    },
  });
}

export function useSendMessage(invitationId: string | undefined, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/messages`, token!, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? "Could not send your message");
      }
      return res.json();
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["opportunity", invitationId, "messages"] }),
  });
}

export interface JobFeedbackBody {
  rating: number;
  wouldWorkAgain: boolean;
  comment?: string;
}

/**
 * Post-job feedback from the pro, over the magic link. Deliberately the
 * PUBLIC route: most pros here have no portal account, and the rotated link
 * in the feedback email is the only way they can reach this at all.
 */
export function useSubmitJobFeedback(invitationId: string | undefined, token: string | null) {
  return useMutation({
    mutationFn: async (body: JobFeedbackBody) => {
      const res = await publicFetch(`/public/opportunities/${invitationId!}/feedback`, token!, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? "Could not save your feedback");
      }
      return res.json();
    },
  });
}
